import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { Portal } from "./portal"

export interface RowActionItem {
  label: string
  onClick: () => void
  icon?: ReactNode
}

export function RowActionsMenu({
  items,
  label = "Row actions",
  disabled = false,
}: {
  items: RowActionItem[]
  label?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const menuId = useId()

  useLayoutEffect(() => {
    if (!open) return

    const updatePosition = () => {
      const trigger = triggerRef.current
      const menu = menuRef.current
      if (!trigger || !menu) return

      const triggerRect = trigger.getBoundingClientRect()
      const menuRect = menu.getBoundingClientRect()
      const gap = 8
      const viewportPadding = 8
      const opensUp = triggerRect.bottom + gap + menuRect.height > window.innerHeight && triggerRect.top - gap - menuRect.height >= viewportPadding
      const top = opensUp ? triggerRect.top - gap - menuRect.height : triggerRect.bottom + gap
      const left = Math.min(
        Math.max(viewportPadding, triggerRect.right - menuRect.width),
        window.innerWidth - menuRect.width - viewportPadding,
      )
      setMenuPosition({ top, left })
    }

    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [items.length, open])

  useEffect(() => {
    if (!open) return

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false)
        setMenuPosition(null)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        setMenuPosition(null)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => {
            if (current) setMenuPosition(null)
            return !current
          })
        }}
        disabled={disabled}
        aria-label={label}
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center justify-center rounded-lg p-2 text-ink/60 transition-colors hover:bg-cream-100 hover:text-ink disabled:pointer-events-none disabled:opacity-40"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-6"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
          />
        </svg>
      </button>

      {open && (
        <Portal>
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            className="fixed z-[70] min-w-48 overflow-hidden rounded-xl border border-gold-500 bg-white p-1.5 text-left shadow-lg"
            style={{
              top: menuPosition?.top ?? 0,
              left: menuPosition?.left ?? 0,
              visibility: menuPosition ? "visible" : "hidden",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  setMenuPosition(null)
                  triggerRef.current?.focus()
                  item.onClick()
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ink transition-colors hover:bg-cream-100"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  )
}
